import React, { useState, useEffect, useCallback } from 'react';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    check: 'M20 6L9 17l-5-5',
    trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6', 'M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2', 'M10 11v6', 'M14 11v6'],
    edit: ['M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7', 'M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z'],
    plus: ['M12 5v14', 'M5 12h14'],
};

const API = '/api';

// Pre-defined countries removed to use database source

function LimitRow({ label, value }) {
    const isUnlimited = value === -1 || value === undefined || value === null;
    return (
        <div className="mpp-limit-row">
            <span className="mpp-limit-label">{label}</span>
            <span className="mpp-limit-value">{isUnlimited ? 'Unlimited' : value}</span>
        </div>
    );
}

export default function ManagePricePlans({ onEdit, onCreate, user }) {
    const [plans, setPlans] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [toast, setToast] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [isAllSelected, setIsAllSelected] = useState(false);

    const uniquePeriods = Array.from(new Set(plans.map(p => p.pricePeriod).filter(Boolean))).sort();

    useEffect(() => {
        if (!selectedPeriod && uniquePeriods.length > 0) {
            setSelectedPeriod(uniquePeriods[0]);
        }
    }, [uniquePeriods, selectedPeriod]);

    const filteredPlans = selectedPeriod
        ? plans.filter(p => p.pricePeriod === selectedPeriod)
        : plans;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchCountries = useCallback(async () => {
        try {
            const res = await fetch(`${API}/countries`);
            const data = await res.json();
            const activeCountries = (Array.isArray(data) ? data : []).filter(c => c.isActive);
            setCountries(activeCountries);
            if (activeCountries.length > 0 && !selectedCountry) {
                setSelectedCountry(activeCountries[0]);
            }
        } catch (err) {
            console.error("Fetch countries error:", err);
        }
    }, [selectedCountry]);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/price-plans`);
            const data = await res.json();
            setPlans(Array.isArray(data) ? data : []);
        } catch {
            setPlans([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCountries();
        fetchPlans();
    }, [fetchCountries, fetchPlans]);

    const handleToggleActive = async (plan) => {
        try {
            const updated = { ...plan, isActive: !plan.isActive };
            const res = await fetch(`${API}/price-plans/${plan._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify(updated),
            });
            if (res.ok) {
                setPlans(prev => prev.map(p => p._id === plan._id ? updated : p));
                showToast(updated.isActive ? 'Plan activated' : 'Plan deactivated');
            }
        } catch {
            showToast('Failed to update status', 'error');
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await fetch(`${API}/price-plans/${id}`, {
                method: 'DELETE',
                headers: {
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                }
            });
            if (res.ok) {
                setPlans(prev => prev.filter(p => p._id !== id));
                showToast('Price plan deleted');
            } else {
                showToast('Failed to delete', 'error');
            }
        } catch {
            showToast('Server error', 'error');
        }
        setDeleteId(null);
    };

    return (
        <div className="mpp-page">
            {/* Toast */}
            {toast && (
                <div className={`pp-toast ${toast.type}`}>
                    {toast.type === 'error' ? (
                        <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={14} strokeWidth={3} style={{ marginRight: 8 }} />
                    ) : (
                        <Icon d={icons.check} size={14} strokeWidth={3} style={{ marginRight: 8 }} />
                    )}
                    {toast.msg}
                </div>
            )}

            {/* Delete confirm Modal */}
            {deleteId && (
                <div className="mpp-modal-overlay">
                    <div className="mpp-modal-box">
                        <div className="mpp-modal-icon">
                            <Icon d={icons.trash} size={42} color="#dd3232" strokeWidth={1.5} />
                        </div>
                        <h3 className="mpp-modal-title">Delete Price Plan?</h3>
                        <p className="mpp-modal-sub">This action cannot be undone.</p>
                        <div className="mpp-modal-actions">
                            <button className="mpp-btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
                            <button className="mpp-btn-confirm" onClick={() => handleDelete(deleteId)}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="mpp-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <h1 className="mpp-title">All Plans</h1>
                    <div className="mpp-country-selector">
                        <select
                            className="mpp-select"
                            value={isAllSelected ? 'All' : selectedCountry?.name || ''}
                            onChange={(e) => {
                                if (e.target.value === 'All') {
                                    setIsAllSelected(true);
                                } else {
                                    setIsAllSelected(false);
                                    const country = countries.find(c => c.name === e.target.value);
                                    setSelectedCountry(country);
                                }
                            }}
                        >
                            <option value="All">All Countries</option>
                            {countries.map(c => (
                                <option key={c.name} value={c.name}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {(user?.userType === 'admin' || user?.permissions?.includes('Create Price Plan')) && onCreate && (
                    <button className="mpp-btn-create" onClick={onCreate}>
                        <Icon d={icons.plus} size={16} strokeWidth={2.5} style={{ marginRight: 8 }} />
                        Create Plan
                    </button>
                )}
            </div>

            {/* Grid Container */}
            <div className="mpp-outer-container">
                {loading ? (
                    <div className="mpp-loading">Loading plans…</div>
                ) : plans.length === 0 ? (
                    <div className="mpp-empty">No plans found. Create one to get started.</div>
                ) : (
                    <div className="mpp-grid">
                        {plans.map((plan) => (
                            <div key={plan._id} className="mpp-card">
                                {/* Card Header (Light Blue) */}
                                <div className="mpp-card-header">
                                    <div className="mpp-card-header-top">
                                        <button
                                            className={`mpp-toggle ${plan.isActive ? 'active' : ''}`}
                                            onClick={() => handleToggleActive(plan)}
                                        >
                                            <div className="mpp-toggle-thumb" />
                                        </button>
                                        <div className="mpp-header-actions">
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Edit Price Plan')) && (
                                                <button className="mpp-action-icon" onClick={() => onEdit(plan)}>
                                                    <Icon d={icons.edit} size={16} />
                                                </button>
                                            )}
                                            {(user?.userType === 'admin' || user?.permissions?.includes('Delete Price Plan')) && (
                                                <button className="mpp-action-icon" onClick={() => setDeleteId(plan._id)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <h2 className="mpp-plan-name">{plan.name}</h2>
                                    <div className="mpp-underline" />
                                </div>

                                {/* Card Body (White) */}
                                <div className="mpp-card-body">
                                    {isAllSelected ? (
                                        <div className="mpp-all-currencies">
                                            {countries.map(c => {
                                                const locale = c.locale || 'en-US';
                                                const rate = c.exchangeRate || c.rate || 1;
                                                const isArabic = locale.includes('nu-arab');
                                                const displayCurrency = isArabic ? (c.engCurrency || '') : (c.currency || '');
                                                const localeForNum = isArabic ? 'en-US' : locale;
                                                return (
                                                    <div key={c.name} className="mpp-currency-row">
                                                        <span className="mpp-cur-label">{c.name}</span>
                                                        <span className="mpp-cur-val">
                                                            {displayCurrency} {(plan.price * rate).toLocaleString(localeForNum, { minimumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="mpp-price" style={{ flexDirection: 'column', gap: '4px' }}>
                                            <div>
                                                <span className="mpp-currency">{selectedCountry?.currency || ''}</span>
                                                {plan.price != null && selectedCountry
                                                    ? (plan.price * (selectedCountry.exchangeRate || selectedCountry.rate || 1)).toLocaleString(selectedCountry.locale || 'en-US', { minimumFractionDigits: 2 })
                                                    : '0.00'}
                                            </div>
                                            {selectedCountry?.locale?.includes('nu-arab') && plan.price != null && (
                                                <div style={{ fontSize: '16.71px', color: '#64748b', fontWeight: '400', letterSpacing: '0' }}>
                                                    ({(plan.price * (selectedCountry.exchangeRate || selectedCountry.rate || 1)).toLocaleString('en-US', { minimumFractionDigits: 2 })} {selectedCountry.engCurrency || ''})
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="mpp-limits">
                                        <LimitRow label="Vehicles Limit" value={plan.vehiclesLimit} />
                                        <LimitRow label="Operators Limit" value={plan.operatorsLimit} />
                                        <LimitRow label="Routes Limit" value={plan.routesLimit} />
                                        <LimitRow label="Users Limit" value={plan.usersLimit} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .mpp-page {
                    padding: 30px;
                    background: #f8fafc;
                    min-height: 100vh;
                }
                .mpp-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .mpp-title {
                    font-size: 22px;
                    font-weight: 600;
                    color: #334155;
                    margin: 0;
                }
                .mpp-btn-create {
                    display: flex;
                    align-items: center;
                    background: #3f51b5;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 6px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 4px 6px -1px rgba(63, 81, 181, 0.2);
                }
                .mpp-btn-create:hover { background: #303f9f; transform: translateY(-1px); }

                .mpp-select {
                    padding: 6px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    background: white;
                    color: #475569;
                    font-size: 14px;
                    outline: none;
                    cursor: pointer;
                    transition: border-color 0.2s;
                }
                .mpp-select:hover { border-color: #3f51b5; }

                .mpp-outer-container {
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    border: 1px solid #e2e8f0;
                }
                .mpp-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 24px;
                }
                @media (max-width: 1200px) { .mpp-grid { grid-template-columns: repeat(3, 1fr); } }
                @media (max-width: 900px) { .mpp-grid { grid-template-columns: repeat(2, 1fr); } }
                @media (max-width: 600px) { .mpp-grid { grid-template-columns: 1fr; } }

                .mpp-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    display: flex;
                    flex-direction: column;
                }

                .mpp-card-header {
                    background: #eef2ff;
                    padding: 24px 20px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    border-bottom: 1px solid #e2e8f0;
                }
                .mpp-card-header-top {
                    width: 100%;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                }
                .mpp-header-actions { display: flex; gap: 12px; }
                .mpp-action-icon {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 0;
                    display: flex;
                    align-items: center;
                    transition: color 0.2s;
                }
                .mpp-action-icon:hover { color: #64748b; }

                /* Toggle Switch */
                .mpp-toggle {
                    width: 44px;
                    height: 24px;
                    background: #cbd5e1;
                    border-radius: 12px;
                    border: none;
                    position: relative;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .mpp-toggle.active { background: #3f51b5; }
                .mpp-toggle-thumb {
                    width: 18px;
                    height: 18px;
                    background: white;
                    border-radius: 50%;
                    position: absolute;
                    top: 3px;
                    left: 3px;
                    transition: transform 0.2s;
                }
                .mpp-toggle.active .mpp-toggle-thumb { transform: translateX(20px); }

                .mpp-plan-name {
                    font-size: 28px;
                    font-weight: 600;
                    color: #475569;
                    margin: 0;
                    letter-spacing: -0.5px;
                }
                .mpp-underline {
                    width: 60%;
                    height: 1px;
                    background: #cbd5e1;
                    margin-top: 24px;
                }

                .mpp-card-body {
                    padding: 24px;
                    background: white;
                    flex-grow: 1;
                }
                .mpp-price {
                    font-size: 36px;
                    font-weight: 600;
                    color: #475569;
                    text-align: center;
                    margin-bottom: 30px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    letter-spacing: -1px;
                }
                .mpp-currency { font-size: 24px; margin-right: 2px; }

                .mpp-limits {
                    display: flex;
                    flex-direction: column;
                    gap: 18px;
                }
                .mpp-limit-row {
                    display: flex;
                    justify-content: space-between;
                    font-size: 13.5px;
                    color: #64748b;
                }
                .mpp-limit-value {
                    font-weight: 500;
                    color: #475569;
                }

                .mpp-all-currencies {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    margin-bottom: 24px;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 16px;
                    max-height: 200px;
                    overflow-y: auto;
                    padding-right: 4px;
                }
                .mpp-all-currencies::-webkit-scrollbar { width: 4px; }
                .mpp-all-currencies::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

                .mpp-currency-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 13px;
                }
                .mpp-cur-label { color: #94a3b8; font-weight: 500; }
                .mpp-cur-val { color: #475569; font-weight: 600; }

                .mpp-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .mpp-modal-box {
                    background: white;
                    padding: 32px;
                    border-radius: 12px;
                    max-width: 400px;
                    width: 90%;
                    text-align: center;
                }

                /* --- Dark Mode Overrides --- */
                .theme-dark .mpp-page {
                    background: #0f0f0f;
                }
                .theme-dark .mpp-title {
                    color: #e2e8f0;
                }
                .theme-dark .mpp-select {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .mpp-outer-container {
                    background: #161616;
                    border-color: #222;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3);
                }
                .theme-dark .mpp-card {
                    background: #1a1a1a;
                    border-color: #242424;
                }
                .theme-dark .mpp-card-header {
                    background: #1e1e1e;
                    border-bottom-color: #242424;
                }
                .theme-dark .mpp-action-icon {
                    color: #64748b;
                }
                .theme-dark .mpp-action-icon:hover {
                    color: #94a3b8;
                }
                .theme-dark .mpp-toggle {
                    background: #2a2a2a;
                }
                .theme-dark .mpp-plan-name {
                    color: #fff;
                }
                .theme-dark .mpp-underline {
                    background: #2a2a2a;
                }
                .theme-dark .mpp-card-body {
                    background: #1a1a1a;
                }
                .theme-dark .mpp-price {
                    color: #fff;
                }
                .theme-dark .mpp-limit-row {
                    color: #888;
                }
                .theme-dark .mpp-limit-value {
                    color: #ccc;
                }
                .theme-dark .mpp-all-currencies {
                    border-color: #242424;
                }
                .theme-dark .mpp-cur-label { color: #64748b; }
                .theme-dark .mpp-cur-val { color: #fff; }
                .theme-dark .mpp-all-currencies::-webkit-scrollbar-thumb { background: #2a2a2a; }
                .theme-dark .mpp-modal-box {
                    background: #1a1a1a;
                    border: 1px solid #242424;
                }
                .theme-dark .mpp-modal-title {
                    color: #fff;
                }
                .theme-dark .mpp-modal-sub {
                    color: #888;
                }
                .theme-dark .mpp-btn-cancel {
                    background: #2a2a2a;
                    color: #ccc;
                }
                .theme-dark .mpp-loading,
                .theme-dark .mpp-empty {
                    color: #555;
                }
            `}</style>
        </div >
    );
}
